/**
 * Resolve the Clerk client SDK once it has finished loading.
 *
 * clerk-js is injected asynchronously, so `window.Clerk` is undefined for the first paint and
 * `clerk.session` is not hydrated for a brief moment after. A request that fires in that window
 * goes out with no Bearer. On the same-apex .co deploy that was masked by the first-party
 * `.paritynow.co` session cookie; on a cross-apex .com deploy that cookie is third-party (blocked
 * in incognito / Safari / Firefox), so the early no-Bearer request hard-fails 401. Awaiting this
 * before reading the token closes that race.
 *
 * Bounded by `timeoutMs` so a missing or broken Clerk never hangs the request forever — after the
 * deadline it resolves whatever it has (possibly null) and the caller proceeds token-less (which
 * still works on the same-apex cookie path, and is a genuine auth failure on cross-apex).
 *
 * Dependencies are injected so this stays unit-testable without a real browser or timers.
 */
export interface GetLoadedClerkOptions {
  getClerk: () => any;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  timeoutMs?: number;
  pollMs?: number;
}

export async function getLoadedClerk(opts: GetLoadedClerkOptions): Promise<any | null> {
  const { getClerk } = opts;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = opts.now ?? (() => Date.now());
  const timeoutMs = opts.timeoutMs ?? 4000;
  const pollMs = opts.pollMs ?? 50;

  const start = now();
  let clerk = getClerk();

  // window.Clerk is assigned only once clerk-js has loaded — poll until it appears.
  while (!clerk && now() - start < timeoutMs) {
    await sleep(pollMs);
    clerk = getClerk();
  }

  if (!clerk) {
    return null;
  }

  // Clerk is present but its session may not be hydrated yet. load() is idempotent and resolves
  // once the session is ready; swallow failures so a Clerk outage degrades to token-less, not a hang.
  if (!clerk.loaded && typeof clerk.load === 'function') {
    try {
      await clerk.load();
    } catch {
      /* proceed token-less */
    }
  }

  return clerk;
}
